/**
 * Petits hooks de chargement de données.
 *
 * Volontairement maison plutôt qu'une bibliothèque de cache : l'application a
 * peu d'écrans, les besoins sont simples (charger, recharger, suivre l'état),
 * et cela évite une dépendance supplémentaire à maintenir. `useResource`
 * annule proprement les requêtes obsolètes pour éviter les états incohérents.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../api/client";

export interface ResourceState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  /** Vrai pendant un rechargement, alors que des données sont déjà affichées. */
  refreshing: boolean;
  reload: () => Promise<void>;
  setData: (updater: T | ((previous: T | null) => T | null)) => void;
}

export function useResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[] = [],
  options: { enabled?: boolean } = {},
): ResourceState<T> {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [refreshing, setRefreshing] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const hasDataRef = useRef(false);

  const run = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    if (hasDataRef.current) setRefreshing(true);
    else setLoading(true);
    try {
      const result = await loaderRef.current(controller.signal);
      if (controller.signal.aborted) return;
      setData(result);
      hasDataRef.current = true;
      setError(null);
    } catch (caught) {
      if (controller.signal.aborted) return;
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError("Une erreur inattendue est survenue.", {
              status: 0,
              code: "internal_error",
            }),
      );
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void run();
    return () => controllerRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, run, ...deps]);

  const update = useCallback(
    (updater: T | ((previous: T | null) => T | null)) => {
      setData((previous) =>
        typeof updater === "function"
          ? (updater as (p: T | null) => T | null)(previous)
          : updater,
      );
    },
    [],
  );

  return { data, error, loading, refreshing, reload: run, setData: update };
}

export interface MutationState<TArgs extends unknown[], TResult> {
  run: (...args: TArgs) => Promise<TResult | null>;
  pending: boolean;
  error: ApiError | null;
  reset: () => void;
  fieldErrors: Record<string, string>;
}

/**
 * Hook d'action (création, modification, suppression).
 *
 * Les erreurs de validation sont exposées par champ (`fieldErrors`) afin que
 * chaque message s'affiche à côté du champ concerné.
 */
export function useMutation<TArgs extends unknown[], TResult>(
  action: (...args: TArgs) => Promise<TResult>,
  options: {
    onSuccess?: (result: TResult) => void | Promise<void>;
    onError?: (error: ApiError) => void;
  } = {},
): MutationState<TArgs, TResult> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const actionRef = useRef(action);
  actionRef.current = action;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const run = useCallback(async (...args: TArgs) => {
    setPending(true);
    setError(null);
    try {
      const result = await actionRef.current(...args);
      await optionsRef.current.onSuccess?.(result);
      return result;
    } catch (caught) {
      const apiError =
        caught instanceof ApiError
          ? caught
          : new ApiError("Une erreur inattendue est survenue.", {
              status: 0,
              code: "internal_error",
            });
      setError(apiError);
      optionsRef.current.onError?.(apiError);
      return null;
    } finally {
      setPending(false);
    }
  }, []);

  const reset = useCallback(() => setError(null), []);

  return {
    run,
    pending,
    error,
    reset,
    fieldErrors: error?.fieldErrors() ?? {},
  };
}

/** Valeur dont la mise à jour est différée (champ de recherche). */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Titre de l'onglet, pour que l'historique du navigateur reste lisible. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} — ArkVibes Gestion`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
